import type { ToolEvent } from "@tsmono/inspect-common/types";

import {
  BROWSER_TOOL_FUNCTIONS,
  buildSelfAnnotation,
  type ScreenshotContent,
  type ToolAnnotation,
} from "../chat/tools/browserActionUtils";

import type { EventNode } from "./types";

export interface VisualActionContext {
  inputScreenshot?: ScreenshotContent[];
  selfAnnotation?: ToolAnnotation;
}

export function computeVisualActionContext(
  eventNodes: EventNode[],
  index: number
): VisualActionContext {
  const node = eventNodes[index];
  if (!node || node.event.event !== "tool") return {};

  const toolEvent = node.event;
  const selfAnnotation = buildSelfAnnotation(
    toolEvent.function,
    toolEvent.arguments
  );
  if (!selfAnnotation) return {};

  // The "before" state for this action is the most recent screenshot the model
  // saw. In computer-use every action (click, type, …) returns a post-action
  // screenshot, so the nearest preceding browser result that contains an image
  // is the correct before-state — not just an explicit `screenshot` action.
  // Gating on `screenshot` alone made consecutive actions all reuse the last
  // standalone screenshot, showing a stale image for every action after it.
  // The flat list inlines subtask/span children, so restrict the scan to the
  // action's own branch: deeper nodes are children of an earlier sibling
  // (a different agent/browser session), and a shallower node means we've
  // left the action's enclosing branch entirely.
  for (let i = index - 1; i >= 0; i--) {
    const candidate = eventNodes[i];
    if (!candidate) continue;
    if (candidate.depth > node.depth) continue;
    if (candidate.depth < node.depth) break;
    if (candidate.event.event !== "tool") continue;
    const candEvent = candidate.event;
    if (!BROWSER_TOOL_FUNCTIONS.has(candEvent.function)) break;
    const inputScreenshot = normalizeScreenshotResult(candEvent.result);
    if (inputScreenshot && inputScreenshot.some((c) => c.type === "image")) {
      return { inputScreenshot, selfAnnotation };
    }
  }

  return {};
}

export function normalizeScreenshotResult(
  result: ToolEvent["result"]
): ScreenshotContent[] | undefined {
  if (Array.isArray(result)) {
    const filtered = result.filter(
      (item): item is ScreenshotContent => item.type !== "document"
    );
    return filtered.length > 0 ? filtered : undefined;
  }
  if (result && typeof result === "object" && "type" in result) {
    if (result.type === "document") return undefined;
    return [result];
  }
  return undefined;
}
